import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAudioTrack } from '../hooks';
import AudioPlayer from '../components/AudioPlayer';
import Header from '../components/Header';
import { cognitoAuthService } from '../services/cognitoAuth';
import { apiService } from '../services/api';
import BackButton from '../components/BackButton';

interface TrackPlayerParams {
  trackId: string;
}

type ExtendedUser = {
  name?: string;
  email?: string;
  subscriptionStatus?:
    | 'free'
    | 'trial'
    | 'active'
    | 'canceled'
    | 'past_due'
    | 'expired';
  trialEndDate?: string;
};

const TrackPlayerPage: React.FC = () => {
  const { t } = useTranslation('tracks');
  const history = useHistory();
  const { trackId } = useParams<TrackPlayerParams>();
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(false);
  const { track, loading, error } = useAudioTrack(trackId, 30, true);

  // Prevent page scroll while on player
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const isAuthenticated = await cognitoAuthService.isAuthenticated();
        if (!isAuthenticated) {
          history.push('/login');
          return;
        }

        setIsLoadingUser(true);
        try {
          const userProfile = await apiService.getUserProfile();
          setUser({
            name: userProfile.name || userProfile.email,
            email: userProfile.email,
            trialEndDate: userProfile.trialEndDate,
          });
        } catch (error) {
          console.error('Error fetching user profile:', error);
          const attributes = await cognitoAuthService.getUserAttributes();
          if (attributes) {
            setUser({
              name: attributes.name || attributes.email,
              email: attributes.email,
              subscriptionStatus: 'free',
              trialEndDate: undefined,
            });
          }
        } finally {
          setIsLoadingUser(false);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setIsLoadingUser(false);
      }
    };

    loadUserData();
  }, [history]);

  const handleTrackEnd = () => {
    // Optionally navigate back to catalog or show completion message
  };

  const handleBackToTracks = () => {
    history.push('/tracks');
  };

  const sessionDurationSeconds = 30 * 60;

  if (loading) {
    return (
      <div
        className='min-h-screen flex items-center justify-center'
        style={{ background: 'rgb(8 6 20 / 100%)' }}
      >
        <div className='text-center animate-fade-in'>
          <div className='spinner glow mx-auto mb-6'></div>
          <p style={{ color: 'var(--text-secondary)' }}>
            {t('preparingSession')}
          </p>
        </div>
      </div>
    );
  }

  if (error || !track) {
    return (
      <div
        className='min-h-screen flex items-center justify-center'
        style={{ background: 'rgb(8 6 20 / 100%)' }}
      >
        <div className='glass-card text-center max-w-md animate-fade-in'>
          <div style={{ color: 'var(--error)', marginBottom: '1rem' }}>
            <svg
              className='w-12 h-12 mx-auto'
              fill='currentColor'
              viewBox='0 0 20 20'
            >
              <path
                fillRule='evenodd'
                d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
                clipRule='evenodd'
              />
            </svg>
          </div>
          <h2
            className='text-xl font-semibold mb-2'
            style={{ color: 'var(--text-primary)' }}
          >
            Error
          </h2>
          <p className='mb-4' style={{ color: 'var(--text-secondary)' }}>
            {error || 'Pista no encontrada'}
          </p>
          <button onClick={handleBackToTracks} className='btn btn-primary'>
            Volver a Pistas
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header showUserInfo={true} />
      <div className='page' style={{ height: '100vh', overflow: 'hidden' }}>
        <div className='player-page-layout'>
          <div
            style={{
              position: 'relative',
              maxWidth: '520px',
              width: '100%',
              margin: '0 auto',
            }}
          >
            <BackButton to='/tracks' className='back-button-absolute' />
          </div>

          <AudioPlayer
            track={track}
            audioUrl={track.type === 'url' ? track.url : undefined}
            playlistContent={track.type === 'hls' ? track.playlist : undefined}
            streamingType={(track.type || 'url') as 'url' | 'hls'}
            sessionDuration={sessionDurationSeconds}
            onTrackEnd={handleTrackEnd}
            showCountdown
          />
        </div>
      </div>
    </>
  );
};

export default TrackPlayerPage;
